<!-- Displays a Journey Planner to begin the journey search process. -->
<script setup lang="ts">
import { useForm } from "vee-validate";
import { ref, onMounted, computed } from "vue";
import type { Ref } from "vue";
import { useJourneyStore } from "@/stores/journey-store";
import { useTicketSummaryStore } from "@/stores/ticket-summary-store";
import type { GetJourneysRequest } from "@/models/remote/GetJourneysRequest";
import { useWindowSize } from "@vueuse/core";
import { JourneyType } from "@/models/JourneyType";
import { useRoute, useRouter } from "vue-router";
import { DateTime } from "luxon";
import StationPicker from "./StationPicker.vue";
import DateTimePicker from "./DateTimePicker.vue";
import RailCardSelector from "./RailCardSelector.vue";
import ViaAvoidSelector from "./ViaAvoidSelector.vue";

const props = defineProps<{
    enableCloseLink?: boolean;
}>();

const { width } = useWindowSize();
const store = useJourneyStore();
const ticketSummaryStore = useTicketSummaryStore();
const route = useRoute();
const router = useRouter();
const emit = defineEmits(["new-search", "close"]);
const isMobile: Ref<boolean> = ref(false);
const hideSummary: Ref<boolean> = ref(false);
const showingViaAvoidSelector: Ref<boolean> = ref(false);
const isEmbedded: Ref<boolean> = ref(false);
const isLoading: Ref<boolean> = ref(false);
const showOutboundDateTimePicker: Ref<boolean> = ref(false);
const showReturnDateTimePicker: Ref<boolean> = ref(false);
const showPassengerRailcardSelector: Ref<boolean> = ref(false);
const errorWithOriginDestination: Ref<boolean> = ref(false);

onMounted(() => {
    isMobile.value = width.value < 760;
    if (route.name === "planner-embed") {
        isEmbedded.value = true;
    }
});

function getInitialValuesFromStore() {
    return {
        originStation: store.query.originStation,
        destinationStation: store.query.destinationStation,
        outboundDate: store.query.outboundDate,
        outboundHour: store.query.outboundDate?.get("hour"),
        outboundMinute: store.query.outboundDate?.get("minute"),
        outboundTimeType: store.query.outboundTimeType,
        returnDate: store.query.returnDate,
        returnHour: store.query.returnDate?.get("hour"),
        returnMinute: store.query.returnDate?.get("minute"),
        returnTimeType: store.query.returnTimeType,
        journeyType: store.query.journeyType,
        adults: store.query.adults,
        children: store.query.children,
        railCard: store.query.railCard,
        numberOfRailcards: store.query.railCardQuantity,
        viaAvoidStation: store.query.viaAvoidStation,
        viaAvoid: store.query.viaAvoid,
    };
}

const initialValues = getInitialValuesFromStore();

const errorsFromSubmit: Ref<Partial<Record<never, string>> | null> = ref(null);
const { handleSubmit, resetForm } = useForm({ initialValues });

const syncStoreAndPinia = () => {
    resetForm({
        values: getInitialValuesFromStore(),
    });
};

const onSubmit = handleSubmit(
    async (values) => {
        errorWithOriginDestination.value = false;

        // Check that the input outbound datetime is not less than now. Set it to now if it is.
        if (store.query.outboundDate < DateTime.now()) {
            store.query.outboundDate = DateTime.now().plus({ minutes: 1 });
        }

        // Check that the input return datetime is not less than now. Set it to now if it is.
        if (store.query.journeyType === JourneyType.Return) {
            if (store.query.returnDate < DateTime.now()) {
                store.query.returnDate = DateTime.now().plus({ minutes: 1 });
            }
        }

        // Check that the origin and destination stations are set
        if (!store.query.originStation || !store.query.destinationStation) {
            errorWithOriginDestination.value = true;
            return;
        }

        // Check that the origin and destination stations are not the same
        if (store.query.originStation.id === store.query.destinationStation.id) {
            errorWithOriginDestination.value = true;
            return;
        }

        const returnDate = store.query.journeyType === JourneyType.Return ? store.query.returnDate?.setZone("utc").toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'") : undefined;

        const returnTimeType = store.query.journeyType === JourneyType.Return ? store.query.returnTimeType : undefined;

        const railCardQuantity = store.query.railCard ? store.query.railCardQuantity : undefined;

        const { bookingId } = route.query;
        const query: GetJourneysRequest = {
            from: store.query.originStation?.id ?? "",
            to: store.query.destinationStation?.id ?? "",
            adults: store.query.adults,
            children: store.query.children,
            railcard: store.query.railCard?.code,
            railcardQuantity: railCardQuantity,
            journeyType: store.query.journeyType,
            outboundDate: store.query.outboundDate.setZone("utc").toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'"),
            outboundTimeType: store.query.outboundTimeType,
            returnDate,
            returnTimeType,
            viaAvoid: store.query.viaAvoid,
            viaAvoidStation: store.query.viaAvoidStation?.id,
        };
        emit("new-search", true);
        ticketSummaryStore.$reset();
        if (bookingId !== undefined) {
            query.bookingId = bookingId as string;
        }
        const target = router.resolve({ name: "searchresults", query }).href;
        if (isEmbedded.value) {
            window.open(target, "_top");
        } else {
            // otherwise just navigate as normal
            await router.push({ name: "searchresults", query });
        }
    },
    ({ errors }) => {
        console.error(errors);
        // happens if invalid, and user tries to submit
        errorsFromSubmit.value = errors;
        hideSummary.value = false;
    }
);

function formatDateForOutInDates(datetime: DateTime) {
    if (!datetime) {
        return "";
    }
    return datetime.toFormat("EEE d MMM");
}

function formatTimeForOutInDates(datetime: DateTime) {
    if (!datetime) {
        return "";
    }
    return datetime.toFormat("HH:mm");
}

function swapOriginAndDestination() {
    const temp = store.query.originStation;
    store.query.originStation = store.query.destinationStation;
    store.query.destinationStation = temp;
}

function trimmedStringWithEllipsis(originalString: string) {
    const maxLength = 23;
    if (originalString.length > maxLength) {
        return originalString.substring(0, maxLength) + "...";
    }
    return originalString; // No need to trim if it's already short enough
}

const totalNumberOfPassengers = computed(() => {
    return store.query.adults + store.query.children;
});

defineExpose({
    syncStoreAndPinia,
});
</script>

<template>
    <v-container class="journey-planner-container" :class="{ 'elevation-1': !isEmbedded }">
        <v-overlay v-model="isLoading" contained class="align-center justify-center">
            <v-progress-circular indeterminate :size="70" :width="7" color="primary"> </v-progress-circular>
        </v-overlay>

        <!-- Put the Passengers modal here so it can attach over the main container -->
        <v-overlay :model-value="true" v-if="showPassengerRailcardSelector" contained @click:outside="showPassengerRailcardSelector = false" class="passengers-railcards-selection-overlay" scrim="white" opacity="1">
            <div>
                <v-card class="px-sm-4 py-sm-4" flat>
                    <v-card-text>
                        <div class="d-flex justify-space-evenly align-center">
                            <div class="adults-selection">
                                <h3>Adults</h3>
                                <v-icon :disabled="store.query.adults === 0 || totalNumberOfPassengers === 1" size="38" @click="store.query.adults--" icon="mdi-minus-circle-outline" opacity="0.5"></v-icon><span class="adults-text">{{ store.query.adults }}</span
                                ><v-icon size="38" @click="store.query.adults++" icon="mdi-plus-circle-outline"></v-icon>
                            </div>

                            <div class="children-selection">
                                <h3>Children</h3>
                                <v-icon :disabled="store.query.children === 0 || totalNumberOfPassengers === 1" size="38" @click="store.query.children--" icon="mdi-minus-circle-outline"></v-icon><span class="children-text">{{ store.query.children }}</span
                                ><v-icon size="38" @click="store.query.children++" icon="mdi-plus-circle-outline"></v-icon>
                            </div>
                        </div>

                        <h2 class="mb-3 mt-7">Railcards</h2>
                        <RailCardSelector></RailCardSelector>

                        <v-btn @click="showPassengerRailcardSelector = false" class="w-100 text-white mt-6" variant="flat" color="var(--vt-c-orange)">Done</v-btn>
                    </v-card-text>
                </v-card>
            </div>
        </v-overlay>

        <!-- Put the Via/Avoid modal here so it can attach over the main container -->
        <ViaAvoidSelector v-if="showingViaAvoidSelector" @close="showingViaAvoidSelector = false"></ViaAvoidSelector>

        <!-- Put the Date pickers here so it can attach over the main container -->
        <DateTimePicker pickerType="outbound" @close="showOutboundDateTimePicker = false" v-if="showOutboundDateTimePicker"></DateTimePicker>
        <DateTimePicker pickerType="return" @close="showReturnDateTimePicker = false" v-if="showReturnDateTimePicker"> </DateTimePicker>

        <div class="journey-planner-form">
            <div class="journey-type-selector">
                <div @click="store.query.journeyType = JourneyType.OneWay" :class="{ selected: store.query.journeyType === JourneyType.OneWay }">Single</div>
                <div @click="store.changeJourneyTypeToReturn()" :class="{ selected: store.query.journeyType === JourneyType.Return }">Return</div>
                <div @click="store.query.journeyType = JourneyType.OpenReturn" :class="{ selected: store.query.journeyType === JourneyType.OpenReturn }">Open Return</div>
            </div>

            <div class="from-to-section" :class="{ error: errorWithOriginDestination }">
                <div class="from">From</div>
                <div class="origin-picker">
                    <StationPicker v-model="store.query.originStation" placeholder="Station" :hide-border="true"> </StationPicker>
                </div>

                <v-divider style="grid-row: 2/2; grid-column: 1/3"></v-divider>

                <div class="to">To</div>
                <div class="destination-picker">
                    <StationPicker v-model="store.query.destinationStation" placeholder="Station" :hide-border="true"> </StationPicker>
                </div>
                <div class="swap-icon" @click="swapOriginAndDestination()">
                    <svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 152.66 127.62">
                        <path d="M106.7,50.75v.02s.8,2.03.8,2.03l-13.08,4.96c-3.24,2.78-3.25,4.99-.15,7.75l42.61,25.17,15.78-47.33c-.04-3.47-2.17-5.35-5.42-5.64l-13.06,4.96-.8-2.03s0,0,0,0c-2.27-5.76-11.39-25.28-33.87-35.23-22.48-9.95-43.08-3.56-48.85-1.37l-8.66,3.29c8.43-.11,18.3,1.25,28.53,5.78,24,10.62,33.75,31.46,36.18,37.64Z" />
                        <path d="M46.47,73.73v-.02s-.61-2.09-.61-2.09l13.48-3.72c3.49-2.47,3.7-4.67.87-7.7L20.13,31.16.03,76.82c-.28,3.46,1.67,5.53,4.87,6.11l13.46-3.72.61,2.09s0,0,0,.01c1.72,5.95,8.99,26.23,30.44,38.22,21.46,12,42.56,7.55,48.52,5.9l8.93-2.47c-8.4-.67-18.1-2.95-27.87-8.41-22.91-12.81-30.68-34.47-32.52-40.84Z" />
                    </svg>
                </div>
            </div>

            <div class="add-via-avoid" @click="showingViaAvoidSelector = true">
                <span v-if="store.query.viaAvoidStation">{{ store.query.viaAvoid }} {{ store.query.viaAvoidStation.name }}</span>
                <span v-else>+ Via/Avoid</span>
            </div>

            <div class="date-section" style="position: relative">
                <div class="out">Out</div>
                <div class="out-datetime" @click="showOutboundDateTimePicker = true">
                    <div class="out-date">{{ formatDateForOutInDates(store.query.outboundDate) }}</div>
                    •
                    <div class="out-time">{{ formatTimeForOutInDates(store.query.outboundDate) }}</div>
                </div>

                <v-divider style="grid-row: 2/2; grid-column: 1/3"></v-divider>

                <template v-if="store.query.journeyType === JourneyType.Return">
                    <div class="in">In</div>
                    <div class="in-datetime" @click="showReturnDateTimePicker = true">
                        <div class="in-date">{{ formatDateForOutInDates(store.query.returnDate) }}</div>
                        •
                        <div class="in-time">{{ formatTimeForOutInDates(store.query.returnDate) }}</div>
                    </div>
                </template>
                <template v-else>
                    <div @click="store.changeJourneyTypeToReturn()" class="add-return">+ Add a return</div>
                </template>
            </div>

            <div class="passenger-section" @click="showPassengerRailcardSelector = true">
                <div class="passengers">
                    {{ store.query.adults }} Adult<span v-if="store.query.adults !== 1">s</span><span v-if="store.query.children > 0">, {{ store.query.children }} Child<span v-if="store.query.children > 1">ren</span></span>
                </div>

                <div class="railcards">
                    <span v-if="typeof store.query.railCardQuantity !== 'undefined' && store.query.railCardQuantity > 0 && store.query.railCard">{{ trimmedStringWithEllipsis(store.query.railCard?.name) }} x {{ store.query.railCardQuantity }}</span>
                    <span v-else>Add railcards</span>
                </div>

                <div class="position-relativee"></div>
            </div>

            <v-btn @click="onSubmit" block flat color="var(--vt-c-orange)" class="mt-4 text-white">Get cheapest tickets</v-btn>

            <div class="close-text-link" v-if="props.enableCloseLink" @click="emit('close')">Close</div>
        </div>
    </v-container>
</template>

<style scoped lang="scss">
.journey-planner-container {
    position: relative;
    margin: 0;
    padding: 0;
    border-radius: 10px;
    background-color: white;
}

.journey-planner-container .journey-planner-form {
    padding: 31px 37px;
}

.v-date-picker {
    z-index: 999;
}

.journey-type-selector {
    display: flex;
    font-weight: 200;
    column-gap: 20px;
    margin-bottom: 12px;
    cursor: pointer;
}

.journey-type-selector .selected {
    font-weight: 500;
}

.add-via-avoid {
    color: #ff7862;
    margin: 8px 0;
    cursor: pointer;
}

.from-to-section,
.date-section,
.passenger-section {
    display: grid;
    grid-template-columns: 45px 1fr auto;
    grid-template-rows: 1fr auto 1fr;
    background-color: #f2f4f3;
    padding: 0 8px;
    align-items: center;
    border-radius: 4px;
}

/*.from-to-section {
    padding-right: 0;
}*/

.date-section {
    grid-template-columns: 45px 1fr;
    grid-template-rows: 1fr auto 1fr;
}

.from-to-section .from,
.date-section .out {
    grid-row: 1/1;
    grid-column: 1/1;
}

.from-to-section .origin-picker,
.date-section .out-datetime {
    grid-row: 1/1;
    grid-column: 2/2;
}

.from-to-section .to,
.date-section .in {
    grid-row: 3/3;
    grid-column: 1/1;
}

.from-to-section .from,
.from-to-section .to,
.date-section .out,
.date-section .in {
    font-size: 0.75rem;
    padding: 15px 0 15px 5px;
}

.from-to-section .destination-picker,
.date-section .in-datetime {
    grid-row: 3/3;
    grid-column: 2/2;
}

.from-to-section .swap-icon {
    grid-row: 1/4;
    grid-column: 3/3;
    opacity: 0.6;
    cursor: pointer;
    width: 33px;
    padding: 0 3px 0 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    /*grid-row: 1 / 4;
    grid-column: 3 / 3;
    opacity: 0.5;
    cursor: pointer;*/
}

.date-section .add-return {
    grid-row: 3/3;
    grid-column: 2/2;
    cursor: pointer;
}

.from-to-section .origin-picker,
.from-to-section .destination-picker,
.date-section .out-datetime,
.date-section .in-datetime,
.passenger-section .passengers,
.passengers-railcards-selection-overlay .v-card h2 {
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
}

.from-to-section .origin-picker :deep(.v-input .v-field--appended),
.from-to-section .destination-picker :deep(.v-input .v-field--appended) {
    padding: 0;
}

.from-to-section .origin-picker :deep(.v-input .v-field__input),
.from-to-section .destination-picker :deep(.v-input .v-field__input) {
    padding-left: 4px;
    padding-right: 4px;
}

.from-to-section .origin-picker :deep(.v-input .v-field__clearable),
.from-to-section .destination-picker :deep(.v-input .v-field__clearable) {
    margin: 0;
}

.date-section .out-datetime,
.date-section .in-datetime {
    display: flex;
    column-gap: 9px;
}

.passenger-section {
    margin-top: 12px;
    padding: 6px 0;
}

.passenger-section .passengers {
    grid-row: 1/1;
    grid-column: 2/2;
}

.passenger-section .railcards {
    grid-row: 3/3;
    grid-column: 2/2;
}

.passengers-railcards-selection-overlay .adults-selection,
.passengers-railcards-selection-overlay .children-selection {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
}

.passengers-railcards-selection-overlay .adults-selection h3,
.passengers-railcards-selection-overlay .children-selection h3 {
    flex-basis: 100%;
    font-weight: 400;
    font-size: 0.93rem;
    margin-bottom: 5px;
}

.passengers-railcards-selection-overlay .adults-text,
.passengers-railcards-selection-overlay .children-text {
    font-size: 1.5rem;
    margin: 0 10px;
}

.passengers-railcards-selection-overlay .adults-selection .v-icon :not(.passengers-railcards-selection-overlay .adults-selection .v-icon.v-icon--disabled),
.passengers-railcards-selection-overlay .children-selection .v-icon :not(.passengers-railcards-selection-overlay .children-selection .v-icon.v-icon--disabled) {
    opacity: 0.85;
}

.passengers-railcards-selection-overlay > :deep(.v-overlay__content) {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
}

.passenger-section .v-overlay > .v-overlay__content > .v-card {
    box-shadow: 0px 0px 28px 0px #686868 !important;
}

.close-text-link {
    color: var(--vt-c-orange);
    text-align: center;
    margin: 13px 0 -13px 0;
    cursor: pointer;
    text-decoration: underline;
}

.from-to-section.error {
    border: 3px solid red;
    animation: flash-border 1s linear 2;
    box-shadow: 0 0 5px rgb(255 0 0 / 22%), 0 0 10px rgb(255 0 0 / 8%);
}

@keyframes flash-border {
    0% {
        border-color: red;
    }

    50% {
        border-color: transparent;
    }

    100% {
        border-color: red;
    }
}

@media (max-width: 760px) {
    .journey-planner-container .journey-planner-form {
        padding-left: 20px;
        padding-right: 20px;
    }
}
</style>
